import i18n from "@/Plugins/i18n";

import type { EventFilter } from "@/Modules/Chat/WebSocketManager";
import type { DocumentChatRoomItemMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import type { DocumentChatRoomItem } from "@/Modules/Chat/Models/Domain/Chat";
import type { ChatStore } from "@/Modules/Chat/Store";
import type { UserStore } from "@/Modules/User/Store";
import { ChatRoomItemTypeMessageWebSocket } from "@/Modules/Chat/Models/WebSocket";
import { ChatRoomItemType } from "@/Modules/Chat/Models/Domain/Chat";

import useChatStore from "@/Modules/Chat/Store";
import useUserStore from "@/Modules/User/Store";
import useAppStore, { type AppStore } from "@/Modules/App/Store";

import { APP_ROUTES } from "@/Generated/Routes/paths";

import { hasValue, isObject } from "@/Utils/Objects";
import { addNewEventWithNeutrals } from "@/Modules/Chat/MessageFilters/helpers/AddNeutralDateEvent";
import { unixSecondsToDate } from "@/Utils/Date";

export interface ExtraParamsDocumentChatMessageItem {
  chatStore: ChatStore;
  userStore: UserStore;
  appStore: AppStore;
}

export const isDocumentChatRoomItem = (message: unknown): message is DocumentChatRoomItemMessageWebSocket => {
  return (
    isObject(message) &&
    hasValue(message.etype) &&
    message.etype === ChatRoomItemTypeMessageWebSocket.DOCUMENT
  );
};

export const DocumentChatRoomItemHandler = (
  message: DocumentChatRoomItemMessageWebSocket,
  {
    chatStore = useChatStore(),
    userStore = useUserStore(),
    appStore = useAppStore(),
  }: ExtraParamsDocumentChatMessageItem
) => {
  const documentChatRoomItem = transformDocumentChatMessageWebSocketToDocumentChatRoomItem(
    message,
    userStore.user?.id
  );

  if (hasValue(chatStore.rooms[message.chat])) {
    const events = addNewEventWithNeutrals(chatStore.rooms[message.chat]!.events, documentChatRoomItem);

    if (!message.old) {
      appStore.notificate({
        title: chatStore.rooms[message.chat]!.user.username,
        body: i18n.global.t("browser-notifications.new_file"),
        redirect: userStore.getUserIsTherapist ? { name: APP_ROUTES.chat.root.name } : undefined,
        extraParams: chatStore.rooms[message.chat]!.user.id,
      });
    }

    chatStore.rooms[message.chat]!.events = events;
  }

  return Promise.resolve(documentChatRoomItem);
};

export const transformDocumentChatMessageWebSocketToDocumentChatRoomItem = (
  documentChatItemMessageWebSocket: DocumentChatRoomItemMessageWebSocket,
  userId = ""
): DocumentChatRoomItem => {
  const isMine = userId === documentChatItemMessageWebSocket.user;
  const type = isMine ? ChatRoomItemType.OUTGOING_TEXT : ChatRoomItemType.INCOMING_TEXT;
  const createdAt = unixSecondsToDate(documentChatItemMessageWebSocket.created);
  const documentChatRoomItem: DocumentChatRoomItem = {
    id: documentChatItemMessageWebSocket.eid,
    createdAt,
    fileName: documentChatItemMessageWebSocket.file_name,
    url: documentChatItemMessageWebSocket.url,
    type,
    old: !!documentChatItemMessageWebSocket.old,
    size: documentChatItemMessageWebSocket.size,
    isReceived: false,
  };

  if (type === ChatRoomItemType.OUTGOING_TEXT) {
    documentChatRoomItem.isConfirmed = true;
  }

  return documentChatRoomItem;
};

export default {
  isType: isDocumentChatRoomItem,
  handler: DocumentChatRoomItemHandler,
} as EventFilter<ExtraParamsDocumentChatMessageItem>;
